// frontend/src/pages/Dashboard/Dashboard.jsx - VERSIÓN CORREGIDA

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LineChart from '../../components/Charts/LineChart';  
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Progress,
  Badge,
  Button,
  ButtonGroup,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { ordersAPI, quotesAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { parseDate } from '../../utils/dates';

function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtros
  const [period, setPeriod] = useState('month'); // month, week, all
  const [selectedSeller, setSelectedSeller] = useState(null);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadDashboardData();
      loadSellers();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, period, selectedSeller]);

  const loadSellers = async () => {
    try {
      const response = await usersAPI.getAll({ 
        page_size: 100,
        is_seller: true,
        is_approved: true,
        is_active: true,
      });
      
      console.log('📥 Respuesta de usuarios:', response.data);
      
      const data = response.data.users || response.data.results || response.data || [];
      console.log('✅ Vendedores aprobados recibidos:', data.length);
      
      setSellers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error cargando vendedores:', error);
      setSellers([]);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Parámetros de filtro
      const params = { period };
      if (selectedSeller) {
        params.seller_id = selectedSeller;
      }
      
      // Obtener estadísticas
      const statsResponse = await ordersAPI.getDashboardStats(params);
      setStats(statsResponse.data.stats);
      
      // ✅ CORRECCIÓN: Aplicar filtro de vendedor a la actividad reciente
      const activityParams = { 
        ordering: '-created_at', 
        page_size: 5 
      };
      
      // ✅ Agregar filtro de vendedor si está seleccionado
      if (selectedSeller) {
        activityParams.seller = selectedSeller;
      }
      
      // Obtener actividad reciente (propuestas + órdenes)
      const [quotesRes, ordersRes] = await Promise.all([
        quotesAPI.getAll(activityParams),
        ordersAPI.getAll(activityParams),
      ]);
      
      const quotes = (quotesRes.data.results || quotesRes.data || []).map(q => ({
        ...q,
        type: 'quote',
        display_number: `${q.proposal_number} v${q.version}`,
      }));
      
      const orders = (ordersRes.data.results || ordersRes.data || []).map(o => ({
        ...o,
        type: 'order',
        display_number: o.order_number,
      }));
      
      // Combinar y ordenar por fecha
      const combined = [...quotes, ...orders]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);
      
      setRecentActivity(combined);
      
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = () => {
    const labels = {
      week: 'Esta Semana',
      month: 'Este Mes',
      all: 'Por Vida',
    };
    return labels[period] || 'Este Mes';
  };

  const getStatusBadge = (status, type) => {
    if (type === 'quote') {
      const badges = {
        pending: { color: 'warning', text: 'Pendiente', icon: 'ni-time-alarm' },
        accepted: { color: 'primary', text: 'Aceptada', icon: 'ni-check-bold' },
        rejected: { color: 'danger', text: 'Rechazada', icon: 'ni-fat-remove' },
      };
      return badges[status] || badges.pending;
    } else {
      return { color: 'info', text: 'Procesada', icon: 'ni-box-2' };
    }
  };

  if (authLoading || loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando dashboard...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="alert alert-danger shadow mt-5" role="alert">
            <span className="alert-icon"><i className="ni ni-fat-remove"></i></span>
            <span className="alert-text">{error}</span>
            <button 
              className="btn btn-sm btn-outline-danger ms-3"
              onClick={loadDashboardData}
            >
              <i className="ni ni-curved-next me-2"></i>
              Reintentar
            </button>
          </div>
        </Container>
      </>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {/* ============================================ */}
        {/* HEADER CON FILTROS */}
        {/* ============================================ */}
        <Row className="mb-4">
          <Col md="6">
            <h6 className="text-white mb-0">
              <i className="ni ni-chart-bar-32 me-2"></i>
              Dashboard de Ventas - {getPeriodLabel()}
            </h6>
            <p className="text-white text-sm opacity-8 mb-0">
              Resumen de propuestas y órdenes de compra
              {selectedSeller && (
                <span className="badge bg-white text-primary ms-2">
                  <i className="ni ni-single-02 me-1"></i>
                  {sellers.find(s => s.id === selectedSeller)?.full_name || 'Vendedor seleccionado'}
                </span>
              )}
            </p>
          </Col>
          <Col md="6" className="text-end">
            {/* Filtro de Período */}
            <ButtonGroup size="sm" className="me-2">
              <Button
                color={period === 'week' ? 'white' : 'white'}
                outline={period !== 'week'}
                onClick={() => setPeriod('week')}
              >
                <i className="ni ni-time-alarm me-1"></i>
                Semana
              </Button>
              <Button
                color={period === 'month' ? 'white' : 'white'}
                outline={period !== 'month'}
                onClick={() => setPeriod('month')}
              >
                <i className="ni ni-calendar-grid-58 me-1"></i>
                Mes
              </Button>
              <Button
                color={period === 'all' ? 'white' : 'white'}
                outline={period !== 'all'}
                onClick={() => setPeriod('all')}
              >
                <i className="ni ni-world me-1"></i>
                Por Vida
              </Button>
            </ButtonGroup>

            {/* Filtro de Vendedor */}
            <UncontrolledDropdown group size="sm">
              <DropdownToggle caret color="white">
                <i className="ni ni-single-02 me-1"></i>
                {selectedSeller 
                  ? sellers.find(s => s.id === selectedSeller)?.full_name || 'Vendedor'
                  : 'Todos los Vendedores'
                }
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => setSelectedSeller(null)}>
                  <i className="ni ni-world me-2"></i>
                  Todos los Vendedores
                </DropdownItem>
                <DropdownItem divider />
                {sellers.map(seller => (
                  <DropdownItem 
                    key={seller.id}
                    onClick={() => setSelectedSeller(seller.id)}
                    active={selectedSeller === seller.id}
                  >
                    <i className="ni ni-single-02 me-2"></i>
                    {seller.full_name || `${seller.first_name} ${seller.last_name}`}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </UncontrolledDropdown>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* CARDS DE ESTADÍSTICAS */}
        {/* ============================================ */}
        <Row>
          {/* Card 1: Propuestas Enviadas */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Propuestas Enviadas
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {stats.quotes.total}
                      </h5>
                      <p className="mb-0">
                        <span className={`text-${stats.quotes.change_percent >= 0 ? 'success' : 'danger'} text-sm font-weight-bolder`}>
                          <i className={`ni ni-bold-${stats.quotes.change_percent >= 0 ? 'up' : 'down'} me-1`}></i>
                          {Math.abs(stats.quotes.change_percent).toFixed(1)}%
                        </span>
                        <span className="text-xs text-muted"> vs período anterior</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                      <i className="ni ni-single-copy-04 text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 2: Tasa de Conversión */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Tasa de Conversión
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {stats.quotes.conversion_rate.toFixed(1)}%
                      </h5>
                      <p className="mb-0">
                        <span className="text-success text-sm font-weight-bolder">
                          {stats.orders.total} órdenes
                        </span>
                        <span className="text-xs text-muted"> de {stats.quotes.total} propuestas</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                      <i className="ni ni-chart-pie-35 text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 3: Órdenes Recibidas */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Órdenes Recibidas
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {stats.orders.total}
                      </h5>
                      <p className="mb-0">
                        <span className={`text-${stats.orders.change_percent >= 0 ? 'success' : 'danger'} text-sm font-weight-bolder`}>
                          <i className={`ni ni-bold-${stats.orders.change_percent >= 0 ? 'up' : 'down'} me-1`}></i>
                          {Math.abs(stats.orders.change_percent).toFixed(1)}%
                        </span>
                        <span className="text-xs text-muted"> vs período anterior</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-info shadow-info text-center rounded-circle">
                      <i className="ni ni-box-2 text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 4: Valor Total */}
          <Col xl="3" sm="6">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Valor Total Órdenes <span className="text-xxs">(sin IVA)</span>
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        ${stats.orders.total_amount.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                      </h5>
                      <p className="mb-0">
                        <span className={`text-${stats.orders.amount_change_percent >= 0 ? 'success' : 'danger'} text-sm font-weight-bolder`}>
                          <i className={`ni ni-bold-${stats.orders.amount_change_percent >= 0 ? 'up' : 'down'} me-1`}></i>
                          {Math.abs(stats.orders.amount_change_percent).toFixed(1)}%
                        </span>
                        <span className="text-xs text-muted"> vs período anterior</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-warning shadow-warning text-center rounded-circle">
                      <i className="ni ni-money-coins text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* SECCIÓN DE GRÁFICAS Y PROGRESO */}
        {/* ============================================ */}
        <Row className="mt-4">
          {/* Gráfica de Evolución */}
          <Col lg="7" className="mb-lg-0 mb-4">
            <Card className="shadow border-0 z-index-2 h-100">
              <CardHeader className="pb-0 pt-3 bg-transparent border-0">
                <h6 className="text-capitalize mb-0">
                  <i className="ni ni-chart-bar-32 me-2"></i>
                  Evolución del {getPeriodLabel()}
                </h6>
                <p className="text-sm mb-0 mt-2">
                  <i className="fa fa-arrow-up text-success me-1"></i>
                  <span className="font-weight-bold">Tendencia positiva</span> en propuestas y órdenes
                </p>
              </CardHeader>
              <CardBody className="p-3">
                <div className="chart">
                  {stats.daily_evolution && stats.daily_evolution.length > 0 ? (
                    <LineChart 
                      data={stats.daily_evolution} 
                      title="Evolución del Mes"
                    />
                  ) : (
                    <div
                      style={{ height: '300px' }}
                      className="d-flex flex-column align-items-center justify-content-center bg-gradient-light border-radius-lg"
                    >
                      <i className="ni ni-chart-bar-32 text-muted" style={{ fontSize: '3rem' }}></i>
                      <p className="text-muted mb-0 mt-3 text-center">
                        No hay datos suficientes para mostrar la gráfica
                        <br />
                        <small>Sube más propuestas y órdenes para ver tendencias</small>
                      </p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Embudo de Conversión */}
          <Col lg="5">
            <Card className="shadow border-0 h-100">
              <CardHeader className="pb-0 p-3 border-0">
                <h6 className="mb-0">
                  <i className="ni ni-chart-pie-35 me-2"></i>
                  Embudo de Conversión
                </h6>
                <p className="text-xs text-muted mb-0 mt-1">
                  {getPeriodLabel().toLowerCase()}
                </p>
              </CardHeader>
              <CardBody className="p-3">
                {(() => {
                  const enviadas = stats.quotes.total || 0;
                  const aceptadas = stats.quotes.accepted || 0;
                  const ordenes = stats.orders.total || 0;
                  const conFact = stats.orders.with_schedule || 0;
                  const tasaAcept = enviadas > 0 ? (aceptadas / enviadas) * 100 : 0;
                  const tasaConv = enviadas > 0 ? (ordenes / enviadas) * 100 : 0;
                  const tasaFact = ordenes > 0 ? (conFact / ordenes) * 100 : 0;
                  return (
                    <>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="text-xs font-weight-bold text-uppercase text-muted">
                            <i className="ni ni-single-copy-04 me-1 text-primary"></i>Propuestas Enviadas
                          </span>
                          <span className="text-sm font-weight-bolder text-dark">{enviadas}</span>
                        </div>
                        <Progress max="100" value={100} className="progress-sm" color="primary" />
                      </div>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="text-xs font-weight-bold text-uppercase text-muted">
                            <i className="ni ni-check-bold me-1 text-success"></i>Aceptadas
                          </span>
                          <span className="text-sm font-weight-bolder text-dark">
                            {aceptadas} <small className="text-muted">({tasaAcept.toFixed(0)}%)</small>
                          </span>
                        </div>
                        <Progress max="100" value={tasaAcept} className="progress-sm" color="success" />
                      </div>

                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="text-xs font-weight-bold text-uppercase text-muted">
                            <i className="ni ni-box-2 me-1 text-info"></i>Órdenes
                          </span>
                          <span className="text-sm font-weight-bolder text-dark">
                            {ordenes} <small className="text-muted">({tasaConv.toFixed(0)}%)</small>
                          </span>
                        </div>
                        <Progress max="100" value={tasaConv} className="progress-sm" color="info" />
                      </div>

                      <div className="mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="text-xs font-weight-bold text-uppercase text-muted">
                            <i className="ni ni-calendar-grid-58 me-1 text-warning"></i>Con Facturación
                          </span>
                          <span className="text-sm font-weight-bolder text-dark">
                            {conFact} <small className="text-muted">({tasaFact.toFixed(0)}%)</small>
                          </span>
                        </div>
                        <Progress max="100" value={tasaFact} className="progress-sm" color="warning" />
                      </div>

                      <hr className="my-3" />
                      <div className="text-center">
                        <span className="text-xs text-uppercase text-muted d-block">Tasa de conversión</span>
                        <h4 className="font-weight-bolder mb-0 text-info">{tasaConv.toFixed(1)}%</h4>
                        <span className="text-xs text-muted">propuestas → órdenes</span>
                      </div>
                    </>
                  );
                })()}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* TABLA DE ACTIVIDAD RECIENTE */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="pb-0 border-0">
                <Row className="align-items-center">
                  <Col md="6">
                    <h6 className="mb-0">
                      <i className="ni ni-bullet-list-67 me-2"></i>
                      Actividad Reciente
                    </h6>
                    <p className="text-xs text-muted mb-0 mt-1">
                      Últimas propuestas y órdenes procesadas
                      {selectedSeller && (
                        <span className="badge bg-gradient-primary ms-2">
                          Filtrado por vendedor
                        </span>
                      )}
                    </p>
                  </Col>
                  <Col md="6" className="text-end">
                    <Button 
                      color="primary" 
                      size="sm"
                      className="btn-icon shadow-sm"
                      onClick={() => navigate('/admin/quotes')}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-single-copy-04"></i>
                      </span>
                      <span className="btn-inner--text ms-2">Ver Propuestas</span>
                    </Button>
                    <Button 
                      color="info" 
                      size="sm"
                      className="btn-icon shadow-sm ms-2"
                      onClick={() => navigate('/admin/orders')}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-box-2"></i>
                      </span>
                      <span className="btn-inner--text ms-2">Ver Órdenes</span>
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="px-0 pt-0 pb-2">
                <div className="table-responsive p-0">
                  <Table className="align-items-center mb-0" hover>
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Tipo
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Folio/Número
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Cliente
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Vendedor
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Monto
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Estado
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Fecha
                        </th>
                        <th className="text-secondary opacity-7"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentActivity.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <i className="ni ni-bullet-list-67 text-muted" style={{ fontSize: '3rem' }}></i>
                            <p className="text-xs text-secondary mb-0 mt-3">
                              {selectedSeller 
                                ? 'No hay actividad reciente para este vendedor'
                                : 'No hay actividad reciente'
                              }
                            </p>
                          </td>
                        </tr>
                      ) : (
                        recentActivity.map((item) => {
                          const badge = getStatusBadge(item.status, item.type);
                          const isQuote = item.type === 'quote';
                          
                          return (
                            <tr 
                              key={`${item.type}-${item.id}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(isQuote ? `/admin/quotes/${item.id}` : `/admin/orders/${item.id}`)}
                            >
                              {/* Tipo */}
                              <td>
                                <div className="d-flex px-2 py-1">
                                  <div>
                                    <div className={`icon icon-shape icon-sm ${isQuote ? 'bg-gradient-primary' : 'bg-gradient-info'} shadow text-center rounded-circle me-2`}>
                                      <i className={`${isQuote ? 'ni ni-single-copy-04' : 'ni ni-box-2'} text-white text-xs`}></i>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column justify-content-center">
                                    <h6 className="mb-0 text-sm">
                                      {isQuote ? 'Cotización' : 'Orden'}
                                    </h6>
                                  </div>
                                </div>
                              </td>

                              {/* Folio/Número */}
                              <td>
                                <p className="text-sm font-weight-bold mb-0 text-dark">
                                  {item.display_number}
                                </p>
                              </td>

                              {/* Cliente */}
                              <td>
                                <p className="text-sm mb-0 text-dark">
                                  {item.client_name || item.billing_name || 'N/A'}
                                </p>
                                <p className="text-xs text-secondary mb-0">
                                  {item.billing_rfc || 'Sin RFC'}
                                </p>
                              </td>

                              {/* Vendedor */}
                              <td>
                                <p className="text-sm mb-0 text-dark">
                                  {item.seller_name || 'N/A'}
                                </p>
                              </td>

                              {/* Monto */}
                              <td className="align-middle text-center">
                                <span className="text-dark text-sm font-weight-bold">
                                  ${parseFloat(item.subtotal || 0).toLocaleString('es-MX', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}
                                </span>
                              </td>

                              {/* Estado */}
                              {/* Estado */}
                              <td className="align-middle text-center">
                                <Badge color="" className={`badge-sm bg-gradient-${badge.color} shadow-sm`}>
                                  <i className={`${badge.icon} me-1`}></i>
                                  {badge.text}
                                </Badge>
                              </td>

                              {/* Fecha */}
                              <td className="align-middle text-center">
                                <span className="text-secondary text-xs font-weight-bold">
                                  {parseDate(item.created_at).toLocaleDateString('es-MX', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </span>
                              </td>

                              {/* Acción */}
                              <td className="align-middle text-center">
                                <Button
                                  color={isQuote ? 'primary' : 'info'}
                                  size="sm"
                                  className="btn-icon shadow-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(isQuote ? `/admin/quotes/${item.id}` : `/admin/orders/${item.id}`);
                                  }}
                                >
                                  <span className="btn-inner--icon">
                                    <i className=""></i>
                                    ver
                                  </span>
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* SECCIÓN DE TOP PERFORMERS (OPCIONAL) */}
        {/* ============================================ */}
        {stats.top_sellers && stats.top_sellers.length > 0 && (
          <Row className="mt-4">
            <Col lg="6" className="mb-lg-0 mb-4">
              <Card className="shadow border-0">
                <CardHeader className="pb-0 border-0">
                  <h6 className="mb-0">
                    <i className="ni ni-trophy me-2"></i>
                    Top Vendedores
                  </h6>
                  <p className="text-xs text-muted mb-0 mt-1">
                    Por monto de órdenes (sin IVA)
                  </p>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="table-responsive">
                    <Table size="sm" className="align-items-center mb-0">
                      <thead>
                        <tr>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Vendedor
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Propuestas
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Aceptadas
                          </th>
                          <th className="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Monto
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.top_sellers.map((seller, index) => (
                          <tr key={seller.seller__id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className={`icon icon-shape icon-xs ${index === 0 ? 'bg-gradient-warning' : index === 1 ? 'bg-gradient-secondary' : 'bg-gradient-info'} shadow text-center rounded-circle me-2`}>
                                  <span className="text-white text-xs font-weight-bold">
                                    {index + 1}
                                  </span>
                                </div>
                                <p className="text-xs font-weight-bold mb-0">
                                  {seller.seller__first_name} {seller.seller__last_name}
                                </p>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="text-xs font-weight-bold">
                                {seller.total_quotes}
                              </span>
                            </td>
                            <td className="text-center">
                              <Badge color="success" className="badge-sm">
                                {seller.accepted_quotes}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <span className="text-xs font-weight-bold text-dark">
                                ${parseFloat(seller.total_amount || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg="6">
              <Card className="shadow border-0">
                <CardHeader className="pb-0 border-0">
                  <h6 className="mb-0">
                    <i className="ni ni-briefcase-24 me-2"></i>
                    Top Clientes
                  </h6>
                  <p className="text-xs text-muted mb-0 mt-1">
                    Por valor de órdenes
                  </p>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="table-responsive">
                    <Table size="sm" className="align-items-center mb-0">
                      <thead>
                        <tr>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Cliente
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Órdenes
                          </th>
                          <th className="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Monto Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.top_clients.filter(c => c.quote__client__name).map((client, index) => (
                          <tr key={client.quote__client__id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className={`icon icon-shape icon-xs ${index === 0 ? 'bg-gradient-warning' : index === 1 ? 'bg-gradient-secondary' : 'bg-gradient-info'} shadow text-center rounded-circle me-2`}>
                                  <span className="text-white text-xs font-weight-bold">
                                    {index + 1}
                                  </span>
                                </div>
                                <p className="text-xs font-weight-bold mb-0">
                                  {client.quote__client__name}
                                </p>
                              </div>
                            </td>
                            <td className="text-center">
                              <Badge color="info" className="badge-sm">
                                {client.total_orders}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <span className="text-xs font-weight-bold text-dark">
                                ${parseFloat(client.total_amount || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
        
      </Container>
    </>
  );
}

export default Dashboard;
